package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.CollageImageStyle
import com.iregained.site.components.sections.product.styles.KnowMoreLinkStyle
import com.iregained.site.theme.HeadlineTextStyle
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Side Converge
@Composable
private fun Modifier.sideConverge(isVisible: Boolean, isLeft: Boolean, delayMs: Int = 0): Modifier {
    val startX = if (isLeft) (-60).px else 60.px
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else startX).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ProductClinicalNeedSection(data: ProductClinicalNeedSectionData = defaultProductClinicalNeedData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-clinical-need-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.headline.isNotBlank() || data.images.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                if (isDesktop) {
                    Row(
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        ClinicalNeedText(
                            data,
                            Modifier.width(50.percent).padding(right = 40.px).sideConverge(isVisible, isLeft = true)
                        )
                        ClinicalNeedCollage(
                            data.images,
                            Modifier.width(50.percent).padding(left = 40.px)
                                .sideConverge(isVisible, isLeft = false, delayMs = 200)
                        )
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                        ClinicalNeedText(
                            data,
                            Modifier.fillMaxWidth().margin(bottom = 50.px).sideConverge(isVisible, isLeft = true),
                            isMobile = true
                        )
                        ClinicalNeedCollage(
                            data.images, Modifier.fillMaxWidth().sideConverge(isVisible, isLeft = false, delayMs = 200)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ClinicalNeedText(data: ProductClinicalNeedSectionData, modifier: Modifier, isMobile: Boolean = false) {
    val palette = ColorMode.current.toSitePalette()
    val align = if (isMobile) Alignment.CenterHorizontally else Alignment.Start
    Column(modifier = modifier, horizontalAlignment = align) {
        if (data.headline.isNotBlank()) SpanText(
            text = data.headline,
            modifier = HeadlineTextStyle.toModifier().color(palette.brand.primary)
                .fontSize(if (isMobile) 28.px else 42.px).fontWeight(FontWeight.Bold).margin(bottom = 16.px)
        )
        if (data.description.isNotBlank()) Div(
            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText).fontSize(18.px)
                .fontWeight(FontWeight.Medium).lineHeight(1.6).margin(bottom = 40.px)
                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.description; onDispose {} } })
        if (data.ctaText.isNotBlank() && data.ctaLink.isNotBlank()) Link(
            path = data.ctaLink, modifier = Modifier.textDecorationLine(TextDecorationLine.None)
        ) {
            SpanText(
                text = data.ctaText,
                modifier = KnowMoreLinkStyle.toModifier().fontWeight(FontWeight.Bold).fontSize(16.px)
                    .letterSpacing(1.px).cursor(Cursor.Pointer)
            )
        }
    }
}

@Composable
private fun ClinicalNeedCollage(images: List<String>, modifier: Modifier) {
    SimpleGrid(numColumns = numColumns(base = 2), modifier = modifier.gap(16.px)) {
        images.take(4).forEach { src -> key(src) { CollageImage(src) } }
    }
}

@Composable
private fun CollageImage(src: String) {
    val palette = ColorMode.current.toSitePalette()
    if (src.isNotBlank()) {
        Box(
            modifier = Modifier.fillMaxWidth()
            .styleModifier { property("aspect-ratio", "4 / 3") } // Replaced static height!
            .borderRadius(12.px).overflow(Overflow.Hidden)
            .boxShadow(0.px, 10.px, 20.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.08f))) {
            Image(
                src = src,
                modifier = CollageImageStyle.toModifier().unextractable().fillMaxSize().objectFit(ObjectFit.Cover)
            )
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field images (List of Strings):
 * - Aspect Ratio: 4:3 or 1:1 recommended.
 * - Recommended Dimensions: 400x300px or 400x400px.
 * - Max File Size: 150KB per image (WebP or JPEG).
 * - Behavior: Will dynamically crop (ObjectFit.Cover) to fit a fixed 200px height.
 */
data class ProductClinicalNeedSectionData(
    val sectionId: String? = null,
    val headline: String,
    val description: String,
    val ctaText: String,
    val ctaLink: String,
    val images: List<String>
)

val defaultProductClinicalNeedData = ProductClinicalNeedSectionData(
    sectionId = "product-clinical-need-section",
    headline = "Hand Paralysis from Stroke is a Huge Unmet Clinical Need",
    description = "There are over 100 million stroke survivors globally. 70% of the survivors have residual hand paralysis and unfortunately live with this disability for life! MyHand® provides the desperately needed effective & functional rehabilitation for them to regain their function and quality of life. It also caters to other musculoskeletal conditions of the hand and injuries from sports or war.\n",
    ctaText = "KNOW MORE →",
    ctaLink = "/patients",
    images = listOf(
        Res.Images.ProductPage.ClinicalNeed.COLLAGE_HAND,
        Res.Images.ProductPage.ClinicalNeed.COLLAGE_DEVICE_TOP,
        Res.Images.ProductPage.ClinicalNeed.COLLAGE_DEVICE_SIDE,
        Res.Images.ProductPage.ClinicalNeed.COLLAGE_CLINICIAN
    )
)
